import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, Output, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaButtonComponent } from "src/app/shared/ds/eva-button/eva-button.component";
import { EvaTextareaComponent } from "src/app/shared/ds/eva-textarea/eva-textarea.component";

import {
  AiChatError,
  AiChatService,
  FEEDBACK_REASON_LABELS,
  FeedbackInput,
  FeedbackReason,
  FeedbackVote,
} from "../services/ai-chat.service";

export const FEEDBACK_COMMENT_MAX = 1000;

/** Corps envoyé à l'agent : motif et commentaire facultatifs, jamais de motif pour un 👍. */
export function buildFeedback(
  turnId: string,
  vote: FeedbackVote,
  reason: FeedbackReason | null,
  comment: string,
): FeedbackInput {
  const text = comment.trim().slice(0, FEEDBACK_COMMENT_MAX);
  return { turn_id: turnId, vote, reason: vote === "down" ? reason : null, comment: text || null };
}

/** 👍/👎 sous une réponse de l'assistant. 👍 : un clic. 👎 : panneau (motif, texte, mention de consentement). */
@Component({
  selector: "app-ai-feedback",
  standalone: true,
  imports: [CommonModule, FormsModule, EvaButtonComponent, EvaTextareaComponent],
  templateUrl: "./ai-feedback.component.html",
  styleUrls: ["./ai-feedback.component.scss"],
})
export class AiFeedbackComponent {
  private readonly chat = inject(AiChatService);
  private readonly evaService = inject(EvaService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input({ required: true }) conversationId = "";
  /** Un autre tour (fil recyclé, autre conversation) : rien de l'état précédent ne doit rester. */
  @Input({ required: true }) set turnId(value: string) {
    if (value !== this._turnId) this.resetPanel();
    this._turnId = value;
  }
  get turnId(): string {
    return this._turnId;
  }
  private _turnId = "";
  @Input() set vote(value: FeedbackVote | null) {
    this.current.set(value);
  }
  @Output() voted = new EventEmitter<FeedbackVote>();

  readonly current = signal<FeedbackVote | null>(null);
  readonly panelOpen = signal(false);
  readonly sending = signal(false);
  readonly thanks = signal(false);
  reason: FeedbackReason | null = null;
  comment = "";

  readonly maxLength = FEEDBACK_COMMENT_MAX;
  readonly reasons = (Object.keys(FEEDBACK_REASON_LABELS) as FeedbackReason[]).map((value) => ({
    value,
    label: FEEDBACK_REASON_LABELS[value],
  }));

  async up(): Promise<void> {
    if (this.sending()) return;
    this.panelOpen.set(false);
    await this.submit("up");
  }

  down(): void {
    this.thanks.set(false);
    this.panelOpen.set(true);
    // Le panneau s'ouvre sous le bord du fil : on le ramène en vue.
    setTimeout(() =>
      this.host?.nativeElement.querySelector(".ai-feedback__panel")?.scrollIntoView({ block: "nearest" }),
    );
  }

  resetPanel(): void {
    this.panelOpen.set(false);
    this.sending.set(false);
    this.thanks.set(false);
    this.reason = null;
    this.comment = "";
  }

  /** Un seul choix, désélectionnable. */
  pick(reason: FeedbackReason): void {
    this.reason = this.reason === reason ? null : reason;
  }

  async send(): Promise<void> {
    if (await this.submit("down")) this.panelOpen.set(false);
  }

  private async submit(vote: FeedbackVote): Promise<boolean> {
    if (this.sending()) return false;
    this.sending.set(true);
    try {
      await this.chat.sendFeedback(
        this.conversationId,
        buildFeedback(this.turnId, vote, this.reason, vote === "down" ? this.comment : ""),
      );
      this.current.set(vote);
      this.thanks.set(true);
      this.voted.emit(vote);
      return true;
    } catch (e) {
      const message = e instanceof AiChatError ? e.message : "L'assistant ne répond pas.";
      this.evaService.showError(message, "Assistant", e instanceof AiChatError ? e.errorType : String(e));
      return false;
    } finally {
      this.sending.set(false);
    }
  }
}
